/*>
* @license
* Copyright (c) 2014-2022 ABILITY Network, Inc., an INOVALON, Inc. company. All Rights Reserved.
* 
* This computer program is CONFIDENTIAL and a TRADE SECRET of ABILITY Network, Inc.
* The receipt or possession of this program does not convey any rights to use,
* reproduce or disclose its contents in whole or in part, without the specific
* written consent of ABILITY Network, Inc. Any use, reproduction or disclosure of
* this program without the express written consent of ABILITY Network, Inc. is a
* violation of the copyright laws and may subject you to criminal prosecution.
<*/
import IProducstApiClient from './contracts'
import { IArticleInfo, IProductInfo, ISectionInfo } from '../ProductContentStateManager/contracts'
import { IProducts, ISection } from '../ProductStateManager/contracts'
import { injectable } from 'inversify'
import { IApiClient } from '../ApiClient/IApiClient'
import { injectProperty } from '../../configuration/ServiceLocator'
import { ILandingPageStateManager, IProductCategoryList } from '../LandingPageStateManager/contracts'
import jwt_decode from 'jwt-decode'
import { productContentQuery, productSectionListQuery } from './ProductQuery'
import { SettingsManager } from '../../configuration/SettingsManager'
import { IUserSessionMiddleware } from '../middleware/UserSessionMiddleware/contracts'
import axios from 'axios'
import { ErrorHelper } from '../../utils/ErrorHelper'

@injectable()
export class ProductsApiClient implements IProducstApiClient {
	@injectProperty('IApiClient')
	private readonly apiClient!: IApiClient

	@injectProperty('IUserSessionMiddleware')
	private readonly userSessionMiddleware!: IUserSessionMiddleware

	getProductsfromJwt(JwtToken: string): Promise<IProducts[]> {
		// throw new Error('Method not implemented.')
		return new Promise<IProducts[]>(resolve => {
			const productResult = [] as IProducts[]
			this.apiClient.getCMSToken().then((token: string) => {
				const tokenObject: any = jwt_decode(token)
				tokenObject.AbilityProductCategoryClaimV2.productCategories.map((item: any, index: number) => {
					item.HomeIndexProductsViewModel.map((productItem: any, index: number) => {
						productResult.push({
							productCode: productItem.Code,
							productName: productItem.Name,
						} as IProducts)
					})
				})
				productResult.sort((a, b) => (a.productName < b.productName ? -1 : 1))
				resolve(this.getSectionsforProduct(productResult))
			})
		})
	}

	getSectionsforProduct(productList: IProducts[]): Promise<IProducts[]> {
		// Actual code
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}`
		const productsformJWT = Array<string>()
		productList.forEach((product: any, index: number) => {
			productsformJWT.push(JSON.stringify(product.productCode))
		})
		return new Promise<IProducts[]>((resolve, reject) => {
			this.userSessionMiddleware.loadSessionJwt(false).then(x =>
				axios
					.get(url, {
						params: {
							query: productSectionListQuery(productsformJWT),
						},
						headers: {
							Authorization: `Bearer ${x}`,
						},
					})
					.then(response => {
						if (response.data.data.aBILITYPage.length > 0 || response.data.data.pdf.length > 0) {
							const page = response.data.data.aBILITYPage
							const cmsProducts = [] as IProducts[]
							page.map((productItem: any, index: number) => {
								cmsProducts.push({
									productCode: productItem.productContent.productName,
									productName: productItem.productContent.productName,
									sectionList: this.getSectionsfromgraphql(productItem.sectionTags),
								} as IProducts)
							})
							// reading pdf content
							if (response.data.data.pdf && response.data.data.pdf.length > 0) {
								response.data.data.pdf.map((productItem: any, index: number) => {
									if (productItem.pdfDocument)
										cmsProducts.push({
											productCode: productItem.productContent.productName,
											productName: productItem.productContent.productName,
											sectionList: this.getSectionsfromgraphql(productItem.sectionTags),
										} as IProducts)
								})
							}
							const cmsEndResult = [] as IProducts[]
							productList.forEach(product => {
								const cmspr = { productCode: product.productCode, productName: product.productName, sectionList: [] as ISection[] }
								cmsProducts.forEach(cmsProduct => {
									if (cmsProduct.productCode.toLowerCase() === product.productCode.toLowerCase()) {
										if (cmsEndResult.findIndex(x => x.productCode.toLowerCase() === product.productCode.toLowerCase()) === -1 && cmspr.sectionList.length === 0) {
											cmspr.sectionList = cmsProduct.sectionList
										} else {
											cmspr.sectionList = this.combineArray(cmspr.sectionList, cmsProduct.sectionList)
											cmspr.sectionList = cmspr.sectionList.filter((v, i, a) => a.findIndex(t => t.sectionId === v.sectionId) === i)
										}
									}
								})
								cmsEndResult.push(
									{
										productCode: cmspr.productCode,
										productName: cmspr.productName,
										sectionList: cmspr.sectionList.sort((a, b) => (a.sectionName < b.sectionName ? -1 : 1))
									}
								)
							})
							resolve(cmsEndResult)
						} else {
							console.log(`no sections found`)
							const cmsEndResultNoSectionsFound = [] as IProducts[]
							productList.forEach(product => {
								cmsEndResultNoSectionsFound.push({
									productCode: product.productCode,
									productName: product.productName,
									sectionList: [] as ISection[],
								})
							})
							resolve(cmsEndResultNoSectionsFound)
						}
					})
					.catch(error => {
						if (error.response !== null && typeof error.response !== 'undefined' && error.response.status === 401) ErrorHelper.handleApiError(error)
						else reject(error)
					}),
			)
		})
	}

	onlyUnique(value: any, index: number, self: any): any {
		return self.indexOf(value) === index
	}

	combineArray(array1: any, array2: any) {
		return [...array1, ...array2]
	}

	getSectionsfromgraphql(sections: any) {
		const productSections = [] as ISection[]
		if (sections === null || sections === undefined) return productSections
		if (sections.termContentItems.length === 0 || sections.termContentItems === [] || sections.termContentItems[0] === null) return productSections
		sections.termContentItems.map((item: any, index: number) => {
			if (item !== null) {
				productSections.push({
					sectionName: item.displayText,
					sectionId: item.contentItemId, // sections.termContentItemIds[index]
				} as ISection)
			}
		})
		return productSections
	}
	getArticleSectionsfromgraphql(sections: any, articleName: string, articleId: string, path: string, isPdf: boolean) {
		const productArticleSections = [] as ISectionInfo[]
		if (sections === null || sections === undefined) return productArticleSections
		if (sections.termContentItems.length === 0 || sections.termContentItems === [] || sections.termContentItems[0] === null) return productArticleSections
		sections.termContentItems.map((item: any, index: number) => {
			if (item !== null) {
				productArticleSections.push({
					sectionName: item.displayText,
					sectionId: item.contentItemId, //sections.termContentItemIds[index],
					articleList: [
						{
							contentItemId: articleId,
							displayText: articleName,
							path: path,
							IsPdf: isPdf,
						},
					] as IArticleInfo[],
				} as ISectionInfo)
			}
		})
		return productArticleSections
	}

	getProductInfoByProductCode(productCode: string): Promise<IProductInfo> {
		// throw new Error('Method not implemented.')
		return new Promise<IProductInfo>(resolve => {
			resolve(this.getArticlesOfSectionsfromgraphql(productCode))
		})
	}
	getArticlesOfSectionsfromgraphql(productCode: string): Promise<IProductInfo> {
		// Actual code
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}`
		return new Promise<IProductInfo>((resolve, reject) => {
			this.userSessionMiddleware.loadSessionJwt(false).then(x =>
				axios
					.get(url, {
						params: {
							query: productContentQuery(productCode),
						},
						headers: {
							Authorization: `Bearer ${x}`,
						},
					})
					.then(response => {
						if (response.data.data.aBILITYPage.length > 0 || response.data.data.pdf.length > 0) {
							const page = response.data.data.aBILITYPage
							const cmsProductsInfo = [] as IProductInfo[]
							page.map((productItem: any, index: number) => {
								cmsProductsInfo.push({
									productDescription: productItem.productContent.productName + ' Description',
									productName: productItem.productContent.productName,
									sectionList: this.getArticleSectionsfromgraphql(productItem.sectionTags, productItem.displayText, productItem.contentItemId, productItem.path, false),
								} as IProductInfo)
							})
							// reading pdf content
							if (response.data.data.pdf && response.data.data.pdf.length > 0) {
								response.data.data.pdf.map((productItem: any, index: number) => {
									if (productItem.pdfDocument)
										cmsProductsInfo.push({
											productDescription: productItem.productContent.productName + ' Description',
											productName: productItem.productContent.productName,
											sectionList: this.getArticleSectionsfromgraphql(productItem.sectionTags, productItem.displayText, productItem.contentItemId, productItem.pdfDocument.relativePdfPath, true),
										} as IProductInfo)
								})
							}
							const singlecmsProductsInfo = {
								productDescription: cmsProductsInfo[0].productDescription,
								productName: cmsProductsInfo[0].productName,
								sectionList: [] as ISectionInfo[],
							} as IProductInfo
							cmsProductsInfo.forEach(product => {
								product.sectionList.forEach(section => {
									singlecmsProductsInfo.sectionList.push({
										sectionName: section.sectionName,
										sectionId: section.sectionId,
										articleList: [
											{
												contentItemId: section.articleList[0].contentItemId,
												displayText: section.articleList[0].displayText,
												path: section.articleList[0].path, //"path"
												IsPdf: section.articleList[0].IsPdf,
											} as IArticleInfo,
										] as IArticleInfo[],
									} as ISectionInfo)
								})
							})
							singlecmsProductsInfo.productDescription = response.data.data.productDescription.length > 0 ? response.data.data.productDescription[0].wysiwygContent.markup : ''
							singlecmsProductsInfo.productName = cmsProductsInfo[0].productName

							const newSectionInfo = Array<ISectionInfo>()
							singlecmsProductsInfo.sectionList.forEach((section: ISectionInfo, index: number) => {
								if (newSectionInfo.findIndex(x => x.sectionName === section.sectionName) === -1) {
									newSectionInfo.push({
										sectionName: section.sectionName,
										sectionId: section.sectionId,
										articleList: [
											{
												contentItemId: section.articleList[0].contentItemId,
												displayText: section.articleList[0].displayText,
												path: section.articleList[0].path,
												IsPdf: section.articleList[0].IsPdf,
											} as IArticleInfo,
										] as IArticleInfo[],
									} as ISectionInfo)
								} else {
									newSectionInfo[newSectionInfo.findIndex(x => x.sectionName === section.sectionName)].articleList.push({
										contentItemId: section.articleList[0].contentItemId,
										displayText: section.articleList[0].displayText,
										path: section.articleList[0].path,
										IsPdf: section.articleList[0].IsPdf,
									} as IArticleInfo)
								}
							})

							newSectionInfo.sort((a, b) => (a.sectionName < b.sectionName ? -1 : 1))
							const newSectionInfoOrderbyArticle = Array<ISectionInfo>()
							newSectionInfo.forEach(section => {
								newSectionInfoOrderbyArticle.push({
									sectionName: section.sectionName,
									sectionId: section.sectionId,
									articleList: section.articleList.sort((a, b) => (a.displayText < b.displayText ? -1 : 1)),
								} as ISectionInfo)
							})
							singlecmsProductsInfo.sectionList = newSectionInfoOrderbyArticle

							resolve(singlecmsProductsInfo)
						} else {
							resolve({
								productName: productCode,
								productDescription: response.data.data.productDescription.length > 0 ? response.data.data.productDescription[0].wysiwygContent.markup : '',
								sectionList: [] as ISectionInfo[],
							} as IProductInfo)
							// reject(`no sections/articles found`)
						}
					})
					.catch(error => {
						if (error.response !== null && typeof error.response !== 'undefined' && error.response.status === 401) ErrorHelper.handleApiError(error)
						else reject(error)
					}),
			)
		})
	}
}
